import { orderBy } from 'lodash';
import moment from 'moment';
import React, { ReactElement, createContext, useContext, useEffect, useState } from 'react';

import { api } from '../../../api';
import { Session } from '../../../api/types/session';
import { SESSION_CATEGORY, SessionFilterType, SessionFilterTypeOption } from './types';

export type SessionContextType = {
  sessions: Record<string, Session[]>;
  setSessions: (sessions: Record<string, Session[]>) => void;
  filteredSessions: Record<string, Session[]>;
  setFilteredSessions: (filteredSessions: Record<string, Session[]>) => void;
  fetchSessionIsLoading: boolean;
  fetchSessionIsError: boolean;
  filters: {
    selectedFilters: SessionFilterType[];
    setSelectedFilters: (selectedFilters: SessionFilterType[]) => void;
    selectedType: string[];
    setSelectedType: (selectedType: string[]) => void;
    selectedStudent: string[];
    setSelectedStudent: (selectedStudent: string[]) => void;
    selectedSubject: string[];
    setSelectedSubject: (selectedSubject: string[]) => void;
    studentOptions: string[];
    setStudentOptions: (studentOptions: string[]) => void;
    subjectOptions: string[];
    setSubjectOptions: (subjectOptions: string[]) => void;
  };
  sessionTypes: Record<string, string>;
};

export const SessionContext = createContext<SessionContextType | null>(undefined);

export const SessionContextProvider = ({ children }: { children: ReactElement }) => {
  const [sessions, setSessions] = useState<Record<string, Session[]>>({});
  const [filteredSessions, setFilteredSessions] = useState<Record<string, Session[]>>({});
  const [fetchSessionIsLoading, setFetchSessionIsLoading] = useState(true);
  const [fetchSessionIsError, setFetchSessionIsError] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState<SessionFilterType[]>([]);
  const [selectedType, setSelectedType] = useState<string[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string[]>([]);
  const [studentOptions, setStudentOptions] = useState<string[]>([]);
  const [subjectOptions, setSubjectOptions] = useState<string[]>([]);
  const [sessionTypes, setSessionTypes] = useState<Record<string, string>>({});

  const [filterOptionsSet, setFilterOptionsSet] = useState<boolean>(false);

  const {
    data: fetchUpcomingSessions,
    isLoading: fetchUpcomingSessionsIsLoading,
    isError: fetchUpcomingSessionsIsError,
  } = api.session.all.useQuery('upcoming', {
    select: (data) => {
      const filteredSessions = data.filter((s) => {
        return moment(s.startTime).isSameOrAfter(new Date(), 'day');
      });
      return orderBy(filteredSessions, ['startTime'], ['asc']);
    },
  });

  const {
    data: fetchCompletedSessions,
    isLoading: fetchCompletedSessionsIsLoading,
    isError: fetchCompletedSessionsIsError,
  } = api.session.all.useQuery('completed');

  const {
    data: fetchCancelledSessions,
    isLoading: fetchCancelledSessionsIsLoading,
    isError: fetchCancelledSessionsIsError,
  } = api.session.all.useQuery('cancelled');

  useEffect(() => {
    if (fetchUpcomingSessionsIsLoading || fetchCompletedSessionsIsLoading || fetchCancelledSessionsIsLoading)
      setFetchSessionIsLoading(true);
    else setFetchSessionIsLoading(false);

    if (fetchUpcomingSessionsIsError || fetchCompletedSessionsIsError || fetchCancelledSessionsIsError)
      setFetchSessionIsError(true);
    else setFetchSessionIsError(false);

    setSessions({
      [SESSION_CATEGORY.UPCOMING]: fetchUpcomingSessions || [],
      [SESSION_CATEGORY.COMPLETED]: fetchCompletedSessions || [],
      [SESSION_CATEGORY.CANCELLED]: fetchCancelledSessions || [],
    });
    setFilteredSessions({
      [SESSION_CATEGORY.UPCOMING]: fetchUpcomingSessions || [],
      [SESSION_CATEGORY.COMPLETED]: fetchCompletedSessions || [],
      [SESSION_CATEGORY.CANCELLED]: fetchCancelledSessions || [],
    });
  }, [
    fetchUpcomingSessionsIsLoading,
    fetchCompletedSessionsIsLoading,
    fetchCancelledSessionsIsLoading,
    fetchUpcomingSessionsIsError,
    fetchCompletedSessionsIsError,
    fetchCancelledSessionsIsError,
    fetchUpcomingSessions,
    fetchCompletedSessions,
    fetchCancelledSessions,
  ]);

  // Filter students, subjects and types from all the sessions
  // Added filterOptionsSet to prevent this from running on every render
  useEffect(() => {
    if (!fetchSessionIsLoading && sessions && !filterOptionsSet) {
      const sessionTypeSet = Array.from(
        new Set(
          Object.values(sessions)
            .flat()
            .filter(Boolean)
            .map((session) => session.type)
        )
      );
      setSessionTypes(
        Object.fromEntries(
          Object.entries(SessionFilterTypeOption).filter(([_, value]) => sessionTypeSet.includes(value))
        )
      );

      const studentNames = Array.from(
        new Set(
          Object.values(sessions)
            .flat()
            .filter(Boolean)
            .map((session) => session.student)
        )
      ).sort((a, b) => a.localeCompare(b));
      setSelectedStudent(studentNames);
      setStudentOptions(studentNames);

      const subjectNames = Array.from(
        new Set(
          Object.values(sessions)
            .flat()
            .filter(Boolean)
            .map((session) => session.subject)
        )
      ).sort((a, b) => a.localeCompare(b));
      setSelectedSubject(subjectNames);
      setSubjectOptions(subjectNames);

      setFilterOptionsSet(true);
    }
  }, [sessions, fetchSessionIsLoading, filterOptionsSet]);

  // Filter sessions based on the selected filters
  useEffect(() => {
    Object.entries(sessions).forEach(([category, catSessions]) => {
      if (Array.isArray(catSessions)) {
        let filteredSessions = [...catSessions];

        if (selectedFilters.includes(SessionFilterType.TYPE)) {
          filteredSessions =
            selectedType.length > 0 ? filteredSessions.filter((session) => selectedType.includes(session.type)) : [];
        }

        if (selectedFilters.includes(SessionFilterType.STUDENT)) {
          filteredSessions =
            selectedStudent.length > 0
              ? filteredSessions.filter((session) => selectedStudent.includes(session.student))
              : [];
        }

        if (selectedFilters.includes(SessionFilterType.SUBJECT)) {
          filteredSessions =
            selectedSubject.length > 0
              ? filteredSessions.filter((session) => selectedSubject.includes(session.subject))
              : [];
        }

        setFilteredSessions((prev) => ({
          ...prev,
          [category]: filteredSessions,
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, selectedType, selectedStudent, selectedSubject]);

  return (
    <SessionContext.Provider
      value={{
        sessions,
        setSessions,
        filteredSessions,
        setFilteredSessions,
        fetchSessionIsLoading,
        fetchSessionIsError,
        filters: {
          selectedFilters,
          setSelectedFilters,
          selectedType,
          setSelectedType,
          selectedStudent,
          setSelectedStudent,
          selectedSubject,
          setSelectedSubject,
          studentOptions,
          setStudentOptions,
          subjectOptions,
          setSubjectOptions,
        },
        sessionTypes,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const withSessionContextProvider = (Component: React.ComponentType<any>) => {
  const WrappedComponent = (props: any) => (
    <SessionContextProvider>
      <Component {...props} />
    </SessionContextProvider>
  );
  return WrappedComponent;
};

export default function useSessionContext() {
  const context = useContext(SessionContext);

  return context;
}
